import { useFeatureIsOn } from "@growthbook/growthbook-react";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Button, Dialog } from "@platform/design-system";
import { BTX_CURRENT_CARRIER_POLICY_DROPDOWN } from "@platform/feature-flags";
import { BaseBenefitPlanPolicyPeriod } from "@platform/openapi-types";
import React, { useState } from "react";
import { CurrentCarrierPolicySelect } from "@platform/plan-grid-core";

import { buildPolicyPeriodOptions } from "./buildPolicyPeriodOptions";

interface NicknamePlanDialogProps {
  currentNickname: string;
  onConfirm: (
    nickname: string,
    policyPeriod?: BaseBenefitPlanPolicyPeriod,
    currentPolicyUuid?: string,
  ) => void;
  onCancel?: VoidFunction;
  title?: string;
  /** When provided, shows a policy period dropdown (used for duplicate plan) */
  currentPolicyPeriod?: BaseBenefitPlanPolicyPeriod;
  /** Coverage UUID, required for the Current Carrier - Policy lookup (ADR 10.22). */
  coverageId?: string;
  /** Plan type (e.g. "ppo") used to narrow the policy lookup. */
  planType?: string;
}

export const NicknamePlanDialog: React.FC<NicknamePlanDialogProps> = ({
  currentNickname,
  onConfirm,
  onCancel,
  title = "Nickname Plan",
  currentPolicyPeriod,
  coverageId,
  planType,
}) => {
  const showPolicyPeriod = currentPolicyPeriod !== undefined;
  const policyPeriodOptions = buildPolicyPeriodOptions(currentPolicyPeriod);
  const policyDropdownEnabled = useFeatureIsOn(
    BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
  );

  const [value, setValue] = useState(currentNickname);
  const [policyPeriod, setPolicyPeriod] = useState<
    BaseBenefitPlanPolicyPeriod | undefined
  >(showPolicyPeriod ? policyPeriodOptions[0].value : undefined);
  const [currentPolicyUuid, setCurrentPolicyUuid] = useState<
    string | undefined
  >(undefined);

  // For a Current duplicate with the flag on, the Current Carrier - Policy
  // dropdown replaces the nickname field (ADR 10.22).
  const showPolicyDropdown =
    policyDropdownEnabled &&
    policyPeriod === BaseBenefitPlanPolicyPeriod.current &&
    Boolean(coverageId);

  const handleSubmit = (): void => {
    // When the policy dropdown replaces the nickname field, don't send a stale
    // nickname the user may have typed before switching to Current (ADR 10.22).
    onConfirm(
      showPolicyDropdown ? "" : value,
      policyPeriod,
      showPolicyDropdown ? currentPolicyUuid : undefined,
    );
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      title={title}
      actions={
        <>
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="primary">
            OK
          </Button>
        </>
      }
    >
      {showPolicyPeriod && (
        <TextField
          id="policy-period-textfield"
          name="policyPeriod"
          margin="dense"
          label="Coverage Opportunity"
          select
          fullWidth
          value={policyPeriod}
          onChange={(e) =>
            setPolicyPeriod(e.target.value as BaseBenefitPlanPolicyPeriod)
          }
          slotProps={{
            htmlInput: { id: "policy-period-input", name: "policyPeriod" },
          }}
        >
          {policyPeriodOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {showPolicyDropdown && coverageId ? (
        <CurrentCarrierPolicySelect
          coverageId={coverageId}
          planType={planType}
          value={currentPolicyUuid}
          onChange={setCurrentPolicyUuid}
        />
      ) : (
        <TextField
          id="nickname-dialog-textfield"
          name="nickname"
          margin="dense"
          label="Plan Nickname"
          type="text"
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit();
            }
          }}
        />
      )}
    </Dialog>
  );
};

// Set display name for better debugging
NicknamePlanDialog.displayName = "NicknamePlanDialog";
export default NicknamePlanDialog;
